import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'admin' | 'dealer' | 'buyer' | 'moderator' | 'user' | null;

/**
 * Returns the primary role of the current user.
 * Priority: admin > dealer > buyer.
 * Uses the user_roles table (RLS: users can read their own roles).
 */
export const useUserRole = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['user-role'],
    queryFn: async (): Promise<{ role: UserRole; roles: UserRole[]; userId: string | null }> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return { role: null, roles: [], userId: null };

      const { data: rows, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id);

      if (error) {
        console.error('[useUserRole] Error:', error);
        return { role: null, roles: [], userId: session.user.id };
      }

      const roles = (rows || []).map(r => r.role as UserRole);
      // Priority: admin > dealer > buyer > others
      const primary: UserRole =
        roles.includes('admin') ? 'admin' :
        roles.includes('dealer') ? 'dealer' :
        roles.includes('buyer') ? 'buyer' :
        roles[0] || null;

      return { role: primary, roles, userId: session.user.id };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        queryClient.invalidateQueries({ queryKey: ['user-role'] });
      }
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);

  return {
    role: data?.role ?? null,
    roles: data?.roles ?? [],
    isAdmin: data?.role === 'admin',
    isDealer: data?.role === 'dealer' || data?.role === 'admin',
    isBuyer: data?.role === 'buyer',
    isLoading,
  };
};
